import React, { Dispatch, useEffect, useMemo, useState } from 'react';
import {
  FolderType,
  IPagedDataResponse,
  IProduct,
  IStoreState,
  BannerLocations,
  IPersistedCatalogueFilters,
  ISubCategoryWithProductCount,
} from '../../types';
import { connect } from 'react-redux';
import {
  Dropdown,
  BreadcrumbSegment,
  Button,
} from '@orderly/morrisons-component-library';
import { getSubCategories } from '../../api/subCategoriesApi';
import ICatalogueProps from './ICatalogueProps';
import getRetailerDetailsRequest from '../../actions/retailerDetailAction';
import { IGetFolderContentsParams } from '../../api/Documents/getFolderContentsApi';
import { getFolderContentsRequest } from '../../actions/Documents/folderContentsAction';
import { getCurrentTradingPackRequest } from '../../actions/Products/currentTradingPackAction';
import { persistCatalogueFilters } from '../../actions/Catalogue/persistCatalogueFiltersAction';
import { FullPageLayout } from '../../layouts';
import { QUERY_KEYS } from '../../constants';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { getProductPageAsync } from '../../api/productsApi';
import Filters from './components/Filters';
import { getTradingGroups } from '../../api/tradingGroupsApi';
import { navigateTo } from '../../routing/Navigation';
import { getQueryString } from '../../helpers/UrlHelper';
import styles from './Catalogue.module.scss';
import CatalogueProducts from './components/CatalogueProducts';
import { BannerCarousel } from '../../components';

const Catalogue = ({
  match,
  history,
  categories,
  retailerDetails,
  folderContents,
  shoppingList,
  currentTradingPack,
  persistedCatalogueFilters,
  getRetailerDetails,
  getFolderContents,
  getCurrentTradingPack,
  persistFilters,
}: ICatalogueProps) => {
  const staleTime = 1000 * 60 * 5;
  const cacheTime = 1000 * 60 * 10;
  const routeCategory = match.params.category ? parseInt(match.params.category, 10) : -1;
  const routeSubCategory = match.params.subCategory ? parseInt(match.params.subCategory, 10) : -1;
  const TRADING_PACKS_FOLDER = 'Trading Packs';
  const [orderBy, setOrderBy] = useState(persistedCatalogueFilters.orderBy);
  const [displayMode, setDisplayMode] = useState(persistedCatalogueFilters.displayMode);
  const [checkedCategory, setCheckedCategory] = useState(
    routeCategory !== -1 ? routeCategory : persistedCatalogueFilters.checkedCategory,
  );
  const [checkedSubCategories, setCheckedSubCategories] = useState<number[]>(
    routeSubCategory === -1 ? persistedCatalogueFilters.checkedSubCategories : [routeSubCategory],
  );
  const [checkedTradingGroups, setCheckedTradingGroups] = useState<string[]>(
    persistedCatalogueFilters.checkedTradingGroups,
  );
  const [caseSize, setCaseSize] = useState(persistedCatalogueFilters.caseSize);
  const [maxOrderQuantity, setMaxOrderQuantity] = useState(undefined);
  const [showBrochureLink, setShowBrochureLink] = useState(false);
  const [useCurrentShoppingList, setUseCurrentShoppingList] = useState(
    persistedCatalogueFilters.useCurrentShoppingList,
  );
  const [tradingPackChecked, setTradingPackChecked] = useState(
    persistedCatalogueFilters.tradingPackChecked,
  );
  const [tradingPackCheckedCategories, setTradingPackCheckedCategories] = useState<number[]>(
    persistedCatalogueFilters.tradingPackCheckedCategories,
  );
  const [tradingPackCheckedLocations, setTradingPackCheckedLocations] = useState<number[]>(
    persistedCatalogueFilters.tradingPackCheckedLocations,
  );
  const shoppingListId = useCurrentShoppingList ? shoppingList.data.id : -1;
  const tradingPromotionQuery = getQueryString('tradingPromotion');
  const isTradingPromotions = tradingPromotionQuery && tradingPromotionQuery === 'true';
  const [tradingPromotionChecked, setTradingPromotionChecked] = useState(
    isTradingPromotions || persistedCatalogueFilters.tradingPromotionChecked,
  );

  useEffect(
    () => {
      getRetailerDetails();
      getCurrentTradingPack();
    },
    [],
  );

  useEffect(
    () => {
      if (retailerDetails && retailerDetails.data) {
        getPromotionFolderContents();

        const retailerMaxOrderQuantity = retailerDetails.data.meta.MaxOrderQuantity;
        if (!maxOrderQuantity && retailerMaxOrderQuantity) {
          setMaxOrderQuantity(parseInt(maxOrderQuantity, 10));
        }
      }
    },
    [retailerDetails.data],
  );

  useEffect(
    () => {
      const newRouteCategory = match.params.category ? parseInt(match.params.category, 10) : -1;
      const newRouteSubCategory = match.params.subCategory ? parseInt(match.params.subCategory, 10) : -1;

      if (newRouteCategory !== -1) {
        setCheckedCategory(newRouteCategory);
      }

      if (newRouteSubCategory !== -1) {
        setCheckedSubCategories([newRouteSubCategory]);
      }

      if (!tradingPromotionChecked) {
        const newTradingPromotionQuery = getQueryString('tradingPromotion');
        const newIsTradingPromotions = newTradingPromotionQuery && newTradingPromotionQuery === 'true';
        setTradingPromotionChecked(newIsTradingPromotions);
      }
    },
    [match],
  );

  useEffect(
    () => {
      persistFilters({
        checkedCategory,
        checkedSubCategories,
        checkedTradingGroups,
        caseSize,
        orderBy,
        displayMode,
        useCurrentShoppingList,
        tradingPromotionChecked,
        tradingPackChecked,
        tradingPackCheckedCategories,
        tradingPackCheckedLocations,
      });
    },
    [
      checkedCategory,
      checkedSubCategories,
      checkedTradingGroups,
      caseSize,
      orderBy,
      displayMode,
      useCurrentShoppingList,
      tradingPromotionChecked,
      tradingPackChecked,
      tradingPackCheckedCategories,
      tradingPackCheckedLocations,
      persistFilters,
    ],
  );

  const getOrderParam = (orderBy: number) => {
    let orderParam: string = '';

    if (orderBy < 2) {
      orderParam = 'cost';
    } else if (orderBy < 4) {
      orderParam = 'description';
    } else if (orderBy < 6) {
      orderParam = 'category';
    } else if (orderBy < 8) {
      orderParam = 'caseSize';
    }

    return orderParam;
  };

  const orderParam = getOrderParam(orderBy);
  const orderDesc = orderBy % 2 === 1;

  let minimumCaseSize: number = null;
  let maximumCaseSize: number = null;

  switch (caseSize) {
    case '1-7':
      minimumCaseSize = 1;
      maximumCaseSize = 7;
      break;
    case '8-20':
      minimumCaseSize = 8;
      maximumCaseSize = 20;
      break;
    case '21+':
      minimumCaseSize = 21;
      maximumCaseSize = 0;
      break;
  }

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    staleTime,
    cacheTime,
    queryKey: [
      QUERY_KEYS.CATALOGUE,
      checkedCategory,
      orderParam,
      orderDesc,
      minimumCaseSize,
      maximumCaseSize,
      checkedSubCategories,
      checkedTradingGroups,
      shoppingListId,
      tradingPromotionChecked,
      tradingPackChecked,
      tradingPackCheckedCategories,
      tradingPackCheckedLocations,
    ],
    queryFn: ({ pageParam = 0 }) => getProductPageAsync({
      minimumCaseSize,
      maximumCaseSize,
      shoppingListId,
      isAdmin: false,
      startIndex: pageParam,
      searchPhrase: '',
      orderByDescending: orderDesc,
      orderBy: orderParam,
      subCategories: checkedSubCategories,
      tradingGroups: checkedTradingGroups,
      categoryId: checkedCategory,
      pageSize: 100,
      tradingPromotion: tradingPromotionChecked,
      retailPromotion: false,
      currentTradingPack: tradingPackChecked,
      tradingPackCategories: tradingPackCheckedCategories,
      tradingPackLocations: tradingPackCheckedLocations,
    }),
    getNextPageParam: (lastPage: IPagedDataResponse<IProduct[]>, allPages: IPagedDataResponse<IProduct[]>[]) => {
      return lastPage.canLoadMore ? allPages.length * 100 : undefined;
    },
  });

  const { data: subCategories, isLoading: subCategoriesLoading } = useQuery({
    staleTime,
    cacheTime,
    queryKey: [QUERY_KEYS.SUB_CATEGORIES, checkedCategory],
    queryFn: () => getSubCategories({ categoryId: checkedCategory, tradingGroups: [] }),
  });

  const { data: tradingGroups, isLoading: tradingGroupsLoading } = useQuery({
    staleTime,
    cacheTime,
    queryKey: [QUERY_KEYS.TRADING_GROUPS, checkedCategory],
    queryFn: () => getTradingGroups({ categoryId: checkedCategory }),
  });

  useEffect(
    () => {
      if (!subCategoriesLoading && subCategories) {
        const validSubCategoryIds = subCategories.map((sub: ISubCategoryWithProductCount) => sub.id);
        const invalidSubCategories = checkedSubCategories.filter(id => !validSubCategoryIds.includes(id));

        if (invalidSubCategories.length > 0) {
          setCheckedSubCategories([]);
        }
      }
    },
    [subCategories, subCategoriesLoading],
  );

  useEffect(
    () => {
      if (!tradingGroupsLoading && tradingGroups) {
        const invalidTradingGroups = checkedTradingGroups.filter(tg => !tradingGroups.includes(tg));

        if (invalidTradingGroups.length > 0) {
          setCheckedTradingGroups([]);
        }
      }
    },
    [tradingGroups, tradingGroupsLoading],
  );

  const totalRecords = useMemo(
    () => {
      if (!data || !data.pages) {
        return 0;
      }

      const firstPage = data.pages[0];
      return firstPage ? firstPage.totalData : 0;
    },
    [data],
  );

  useEffect(
    () => {
      const { data } = folderContents;
      const { description, documents } = data;

      if (description && description.toLowerCase() === TRADING_PACKS_FOLDER.toLowerCase() && documents.length > 0) {
        setShowBrochureLink(true);
      } else {
        setShowBrochureLink(false);
      }
    },
    [folderContents],
  );

  const getPromotionFolderContents = () => {
    getFolderContents(
      {
        folderPath: TRADING_PACKS_FOLDER,
        retailerId: retailerDetails.data.id,
        prefix: '',
        folderType: FolderType.CustomerGroup,
      },
    );
  };

  const scrollToTop = () => {
    window.scrollTo(0, 0);
  };

  const onCategoryChange = (id: string, checked: boolean) => {
    const categoryId = id.replace('cat_', '');
    history.push(checked ? `/catalogue/${categoryId}` : '/catalogue');
    setCheckedCategory(checked ? parseInt(categoryId, 10) : -1);
    clearCheckedSubCategories();
    clearCheckedTradingGroups();
  };

  const onCaseSizeChange = (caseSize: string, checked: boolean) => {
    setCaseSize(checked ? caseSize : '');
    scrollToTop();
  };

  const onSubCategoryChange = (id: string, checked: boolean) => {
    let newSubCategories = checkedSubCategories;
    const subcategoryId = parseInt(id.replace('sub_', ''), 10);

    if (!checked) {
      newSubCategories = newSubCategories.filter(x => x !== subcategoryId);
    } else {
      newSubCategories.push(subcategoryId);
    }

    setCheckedSubCategories([...newSubCategories]);
    scrollToTop();
  };

  const onTradingGroupChange = (id: string, checked: boolean) => {
    let newTradingGroups = checkedTradingGroups;

    if (!checked) {
      newTradingGroups = checkedTradingGroups.filter(x => x !== id);
    } else {
      newTradingGroups.push(id);
    }

    setCheckedTradingGroups([...newTradingGroups]);
    scrollToTop();
  };

  const onUseCurrentShoppingList = () => {
    setUseCurrentShoppingList(!useCurrentShoppingList);
    scrollToTop();
  };

  const onTradingPromotionChecked = () => {
    setTradingPromotionChecked(!tradingPromotionChecked);
    scrollToTop();
  };

  const onTradingPackCategoryChecked = (id: number, checked: boolean) => {
    let newCheckedCategories: number[] = tradingPackCheckedCategories;

    if (checked) {
      newCheckedCategories.push(id);
    } else {
      newCheckedCategories = newCheckedCategories.filter(x => x !== id);
    }

    setTradingPackCheckedCategories([...newCheckedCategories]);
    scrollToTop();
  };

  const onTradingPackLocationChecked = (id: number, checked: boolean) => {
    let newCheckedLocations: number[] = tradingPackCheckedLocations;

    if (checked) {
      newCheckedLocations.push(id);
    } else {
      newCheckedLocations = newCheckedLocations.filter(x => x !== id);
    }

    setTradingPackCheckedLocations([...newCheckedLocations]);
    scrollToTop();
  };

  const onTradingPackChecked = () => {
    setTradingPackChecked(!tradingPackChecked);
    clearTradingPackCategories();
    clearTradingPackLocations();
  };

  const displayList = () => setDisplayMode('list');
  const displayGrid = () => setDisplayMode('grid');

  const clearCheckedSubCategories = () => {
    setCheckedSubCategories([]);
    scrollToTop();
  };

  const clearCheckedTradingGroups = () => {
    setCheckedTradingGroups([]);
    scrollToTop();
  };

  const clearTradingPackCategories = () => {
    setTradingPackCheckedCategories([]);
    scrollToTop();
  };

  const clearTradingPackLocations = () => {
    setTradingPackCheckedLocations([]);
    scrollToTop();
  };

  const breadcrumbs: BreadcrumbSegment[] = [
    { key: 1, text: 'Catalogue', url: '/catalogue' },
  ];

  return (
  <FullPageLayout
    heading="Catalogue"
    breadcrumbs={breadcrumbs}
    headerPillContent={!isLoading ?
      <div className={styles.totalRecords}>
        {totalRecords} {totalRecords === 1 ? 'Product' : 'Products'}
      </div> :
      <div className={`${styles.totalRecords} ${styles.loadingPill}`}>
        &nbsp;
      </div>
    }
    headerAdditionalContent={
      <>
        <div className={styles.catalogueCarouselWrapper}>
          <BannerCarousel controls={true} location={BannerLocations.Catalogue} />
        </div>
        <div className={styles.headerFilters}>
          {showBrochureLink && (
            <Button
              type="button"
              className="brochure-button link"
              text="Trading Packs"
              iconPos="left"
              icon="icon-folder"
              onClick={() => navigateTo('/documents?folder=Trading%20Packs')}
            />
          )}
          <Dropdown
            className={styles.catalogueSort}
            label=""
            name="catalogue-sort"
            onChange={e => setOrderBy(e.value as number)}
            selectedValue={orderBy}
            options={[
              { label: 'Price: low to high', value: 0 },
              { label: 'Price: high to low', value: 1 },
              { label: 'Description: A-Z', value: 2 },
              { label: 'Description: Z-A', value: 3 },
              { label: 'Category: A-Z', value: 4 },
              { label: 'Category: Z-A', value: 5 },
              { label: 'Case Size: low to high', value: 6 },
              { label: 'Case Size: high to low', value: 7 },
            ]}
          />
          <button
            className={`${styles.displayMode} ${displayMode === 'list' ? 'active' : styles.inactive}`}
            type="button"
            onClick={displayList}
          >
            <span className="icon-list" />
          </button>
          <button
            className={`${styles.displayMode} ${displayMode === 'grid' ? 'active' : styles.inactive}`}
            type="button"
            onClick={displayGrid}
          >
            <span className="icon-grid" />
          </button>
        </div>
      </>
    }
  >
    <div className={styles.container}>
      <div className={styles.filtersAndProducts}>
        <div className={styles.filters}>
          <Filters
            onCaseSizeChange={onCaseSizeChange}
            onSubCategoryChange={onSubCategoryChange}
            onCategoryChange={onCategoryChange}
            onUseCurrentShoppingList={onUseCurrentShoppingList}
            selectedCategory={checkedCategory}
            selectedCaseSize={caseSize}
            categories={categories}
            subCategoriesLoading={subCategoriesLoading}
            subCategories={subCategories}
            checkedSubCategories={checkedSubCategories}
            shoppingList={shoppingList}
            useCurrentShoppingList={useCurrentShoppingList}
            tradingPromotionChecked={tradingPromotionChecked}
            onTradingPromotionChecked={onTradingPromotionChecked}
            tradingGroupsLoading={tradingGroupsLoading}
            tradingGroups={tradingGroups}
            onTradingGroupChange={onTradingGroupChange}
            checkedTradingGroups={checkedTradingGroups}
            tradingPackChecked={tradingPackChecked}
            tradingPackCheckedCategories={tradingPackCheckedCategories}
            tradingPackCheckedLocations={tradingPackCheckedLocations}
            onTradingPackCategoryChecked={onTradingPackCategoryChecked}
            onTradingPackLocationChecked={onTradingPackLocationChecked}
            onTradingPackChecked={onTradingPackChecked}
            currentTradingPack={currentTradingPack}
            clearCheckedSubCategories={clearCheckedSubCategories}
            clearCheckedTradingGroups={clearCheckedTradingGroups}
            clearTradingPackCategories={clearTradingPackCategories}
            clearTradingPackLocations={clearTradingPackLocations}
          />
        </div>
        <div className={styles.productsSection}>
          <CatalogueProducts
            data={data}
            displayMode={displayMode}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            maxOrderQuantity={maxOrderQuantity}
            isFetchingNextPage={isFetchingNextPage}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  </FullPageLayout>
  );
};

const mapStateToProps = (state: IStoreState) => {
  return {
    categories: state.categories,
    folderContents: state.documentsFolderContents,
    retailerDetails: state.retailerDetails,
    shoppingList: state.shoppingList,
    currentTradingPack: state.currentTradingPack,
    persistedCatalogueFilters: state.persistedCatalogueFilters,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => {
  return {
    getRetailerDetails: () => dispatch(getRetailerDetailsRequest()),
    getFolderContents: (parameters: IGetFolderContentsParams) =>
      dispatch(getFolderContentsRequest(parameters, true)),
    getCurrentTradingPack: () => dispatch(getCurrentTradingPackRequest()),
    persistFilters: (filters: IPersistedCatalogueFilters) => dispatch(persistCatalogueFilters(filters)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Catalogue);
